import { Button } from 'components/Button';
import { Meta } from 'components/Meta';
import { Tag } from 'components/Tag';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BookStack3D } from './BookStack3D';
import styles from './Bookshelf.module.css';
import { books } from './data/books';
import { magnifiedPositions, nearestTick, restPositions } from './railFisheye';
import { ShelfSentence } from './ShelfSentence';
import { applyView, DEFAULT_VIEW, options as viewOptions } from './shelfFilter';

/* A date as precise as it was given: "2023", "May 2024", or a full date
   shown as its month. A bare year parsed as a date would read as January. */
const fmt = iso => {
  if (!iso) return null;
  if (/^\d{4}$/.test(iso)) return iso;
  const [year, month = '01'] = iso.split('-');
  return new Date(`${year}-${month}-01T00:00:00`).toLocaleDateString('en-NZ', {
    month: 'long',
    year: 'numeric',
  });
};

/* The rail's pitch per book, in px. It shrinks to fit the screen as the shelf
   grows, down to the smallest spacing a pointer can still land on. */
const RAIL_PITCH = 12;
const RAIL_MIN_PITCH = 5;
const RAIL_SHARE = 0.72;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* The book itself carries the transition: it leaves the pile flat, stands up,
   and turns to its cover, landing exactly on the hero's slot. Everything else
   (the coloured ground, the copy, the rail) is secondary and just fades up
   underneath it.

   Done as FLIP: the travelling book is positioned on its *destination* and
   animated back to where it started, so the landing needs no correction and
   cannot drift. Must match --flightMs and the stylesheet's durations. */
const FLIGHT_MS = 760;


export function Bookshelf({ initialSlug = null }) {
  const router = useRouter();
  const [openSlug, setOpenSlug] = useState(initialSlug);
  /* stack -> flying -> detail, and exit on the way back. Landing straight on
     a book's URL skips the flight: there is nowhere to fly from. */
  const [phase, setPhase] = useState(initialSlug ? 'detail' : 'stack');
  /* Accents sampled off the real cover art, reported by the scene once each
     image decodes. The data file's value is only a fallback. */
  const [accents, setAccents] = useState({});

  const offsetRef = useRef(0);
  const refocusRef = useRef(null);
  const pickListRef = useRef(null);
  const timerRef = useRef([]);
  const readerElRef = useRef(null);
  /* Band geometry and scroll position, handed to the scene by reference. This
     updates on every scroll frame; through state it would re-render the whole
     page on each one. */
  const readerRef = useRef({ active: false, bands: [], scrollY: 0 });
  /* Mirrors openSlug for the router subscription, which is set up once and
     would otherwise close over the value from that render. */
  const openRef = useRef(initialSlug);

  const book = useMemo(() => books.find(b => b.slug === openSlug) || null, [openSlug]);
  /* Frozen at the moment a book is opened. Recomputing it as you scroll would
     reorder the document under your feet the instant the URL changed. */
  const [entrySlug, setEntrySlug] = useState(initialSlug);
  /* Which tick the cursor is on, so the rail can swell around it and the page
     behind can step back. Index, not slug: the wave is about neighbours. */
  const [hovered, setHovered] = useState(null);
  /* The sentence above the pile: which books, from which genre, in what
     order. The reader follows it too, so scrolling on from a book stays
     inside what you filtered to. */
  const [view, setView] = useState(DEFAULT_VIEW);
  const shelf = useMemo(() => applyView(books, view), [view]);
  const shelfSlugs = useMemo(() => shelf.map(b => b.slug), [shelf]);
  const choices = useMemo(() => viewOptions(books, view), [view]);
  const headRef = useRef(null);
  const isOpen = !!book;
  /* The reader is the shelf in its own order, and opening a book scrolls to
     it. It used to rotate the list so the opened book came first, which made
     every book before it unreachable. A book opened by URL that the filter
     hides still gets a band, at the front. */
  const order = useMemo(() => {
    if (!entrySlug || shelf.some(b => b.slug === entrySlug)) return shelf;
    return [books.find(b => b.slug === entrySlug), ...shelf].filter(Boolean);
  }, [entrySlug, shelf]);
  /* The book the reader should open at, consumed by the layout effect below
     once its band exists. */
  const arriveRef = useRef(initialSlug);
  /* A dense shelf packs its rail tighter rather than running off the screen. */
  const [railPitch, setRailPitch] = useState(RAIL_PITCH);
  useEffect(() => {
    const fit = () =>
      setRailPitch(
        Math.max(RAIL_MIN_PITCH, Math.min(RAIL_PITCH, (window.innerHeight * RAIL_SHARE) / order.length))
      );
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [order.length]);

  /* The rail magnifies under the cursor (railFisheye.js). Positions are written
     straight to the ticks: they change on every pointer move, and through
     state that would re-render the whole reader each time. */
  const tabsRef = useRef(null);
  const tickRefs = useRef([]);
  const placeTicks = useCallback(
    cursor => {
      const count = order.length;
      const positions =
        cursor == null
          ? restPositions(count, railPitch)
          : magnifiedPositions(count, railPitch, cursor);
      tickRefs.current.slice(0, count).forEach((tick, i) => {
        if (tick) tick.style.transform = `translateY(${positions[i]}px) translateY(-50%)`;
      });
      return positions;
    },
    [order.length, railPitch]
  );
  useLayoutEffect(() => {
    placeTicks(null);
  }, [placeTicks, isOpen]);

  const clearTimers = () => {
    timerRef.current.forEach(clearTimeout);
    timerRef.current = [];
  };
  const after = (ms, fn) => timerRef.current.push(setTimeout(fn, ms));

  useEffect(() => clearTimers, []);

  /* Opening and closing are the same two steps every time: move the page's
     own state, and put the URL where the state now is. Shallow, because the
     route's getStaticProps has nothing to add -- the scene is already mounted
     and the book is already in it, so a real navigation would tear down the
     canvas mid-flight. */
  const show = useCallback((slug, { reorder = true } = {}) => {
    clearTimers();
    openRef.current = slug;
    arriveRef.current = slug;
    if (reorder) setEntrySlug(slug);
    setOpenSlug(slug);
    if (prefersReducedMotion()) {
      setPhase('detail');
      return;
    }
    /* The scene animates the book itself; the page only waits for it to
       arrive before bringing the copy in. */
    setPhase('flying');
    after(FLIGHT_MS, () => setPhase('detail'));
  }, []);

  const open = useCallback(
    slug => {
      show(slug);
      router.push(`/bookshelf/${slug}/`, undefined, { shallow: true });
    },
    [router, show]
  );

  const onAccent = useCallback((slug, hex) => {
    setAccents(prev => (prev[slug] === hex ? prev : { ...prev, [slug]: hex }));
  }, []);

  const hide = useCallback(() => {
    clearTimers();
    openRef.current = null;
    if (prefersReducedMotion()) {
      setOpenSlug(null);
      setPhase('stack');
      return;
    }
    setPhase('exit');
    /* Focus is lost when the back button unmounts; hand it back to the book
       that was opened once the stack is on screen again. */
    refocusRef.current = openSlug;
    after(FLIGHT_MS, () => {
      setOpenSlug(null);
      setPhase('stack');
    });
  }, [openSlug]);

  const close = useCallback(() => {
    hide();
    router.push('/bookshelf/', undefined, { shallow: true });
  }, [hide, router]);

  /* The browser's own back and forward. Those move the route without going
     through open/close, so the page has to follow the URL rather than assume
     it put it there.

     A subscription to the router rather than a look at router.query each
     render: the query is React state and reacting to it means setting state
     from an effect, which cascades. Router events are an external system, and
     this is what subscribing to one looks like.

     Guarded on the slug actually differing, because our own shallow pushes
     come back through here too and would otherwise restart the flight. */
  useEffect(() => {
    const onRoute = url => {
      const match = /^\/bookshelf\/([^/?#]+)/.exec(url);
      const slug = match ? match[1] : null;
      if (slug === openRef.current) return;
      if (slug) show(slug);
      else hide();
    };
    router.events.on('routeChangeComplete', onRoute);
    return () => router.events.off('routeChangeComplete', onRoute);
  }, [router.events, show, hide]);

  /* The reader takes the whole window: the site nav steps aside and the rail
     takes the left edge it was using. On the document element rather than in
     component state, because the nav is a sibling of this page in the layout
     and cannot be reached from here any other way. */
  useEffect(() => {
    if (!book) return undefined;
    document.documentElement.dataset.reader = '';
    return () => {
      delete document.documentElement.dataset.reader;
    };
  }, [book]);

  /* An effect, not a callback: the focus target only exists after React has
     committed the stack, which is later than any rAF scheduled at close. */
  useEffect(() => {
    if (phase !== 'stack' || !refocusRef.current) return;
    pickListRef.current
      ?.querySelector(`button[data-slug="${refocusRef.current}"]`)
      ?.focus();
    refocusRef.current = null;
  }, [phase]);

  /* Escape leaves. The arrows are the browser's own now -- the reader is a
     real scrolling document, so up and down already move between books and
     hijacking them would only make that worse. */
  useEffect(() => {
    if (!book) return undefined;
    const onKey = e => {
      if (e.key !== 'Escape') return;
      close();
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [book, close]);

  /* Arrive at the opened book. A layout effect, so it lands before the paint
     and before the scroll handler below first reads the position: read at the
     top, it would take the first band as the current book and change the URL
     and the flight's target to it. */
  useLayoutEffect(() => {
    const slug = arriveRef.current;
    const el = readerElRef.current;
    if (!isOpen || !slug || !el) return undefined;
    const band = el.querySelector(`[data-band="${slug}"]`);
    if (!band) return undefined;
    arriveRef.current = null;
    const pin = () => window.scrollTo(0, band.offsetTop);
    pin();

    /* And keep it there while the page settles. Webfonts and cover images
       arriving after this change the height of every band above, which
       slid a directly-loaded book down past the fold and landed you on the
       one before it. Pinned until the reader scrolls for themselves, or the
       layout has had time to settle. */
    const observer = new ResizeObserver(pin);
    observer.observe(el);
    const release = () => {
      observer.disconnect();
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(type =>
        window.removeEventListener(type, release)
      );
    };
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(type =>
      window.addEventListener(type, release, { passive: true })
    );
    const timer = setTimeout(release, 2000);
    return () => {
      clearTimeout(timer);
      release();
    };
  }, [isOpen, openSlug, order]);

  /* The reader drives three things off one scroll handler: the band geometry
     the scene needs, which book is current, and the URL.

     Measured from the DOM rather than assumed, because a band's height is its
     copy's height and no two reviews are the same length. Re-measured on
     resize for the same reason. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = readerElRef.current;
    if (!el) return undefined;

    const measure = () => {
      readerRef.current.bands = [...el.querySelectorAll('[data-band]')].map(node => ({
        slug: node.dataset.band,
        top: node.offsetTop,
        height: node.offsetHeight,
      }));
      /* The space actually left for the book, measured rather than assumed:
         the rail's inset and the copy column both move at breakpoints, and a
         pair of constants in the scene quietly drifted from them. */
      const rail = el.querySelector('nav');
      const copy = el.querySelector('[data-band] > div:last-child');
      readerRef.current.gutter = rail?.getBoundingClientRect().right ?? null;
      readerRef.current.copyLeft = copy?.getBoundingClientRect().left ?? null;

      /* The box the layout has actually left for the book, and where it sits
         within its band. Side by side on a wide screen, above the copy on a
         narrow one -- the stylesheet decides, and the scene follows rather
         than carrying a second opinion about the breakpoint. */
      const band = el.querySelector('[data-band]');
      const hero = band?.firstElementChild;
      if (band && hero) {
        const b = band.getBoundingClientRect();
        const h = hero.getBoundingClientRect();
        readerRef.current.hero = {
          left: h.left,
          width: h.width,
          height: h.height,
          /* How far the hero's middle is from the band's, so the book can be
             parked over the copy rather than beside it. */
          offset: h.top + h.height / 2 - (b.top + b.height / 2),
          /* From the band's top instead, which is the same for every band. */
          inset: h.top - b.top,
        };
      }
    };

    const sync = () => {
      state.active = true;
      state.scrollY = window.scrollY;

      /* Whichever band holds the middle of the screen is the one you are
         reading, and that is the one the URL should name. */
      const eye = window.scrollY + window.innerHeight / 2;
      const current = state.bands.find(b => eye >= b.top && eye < b.top + b.height);
      /* Nothing to follow once the reader is closing: a scroll still easing
         to a stop would otherwise put a book back in the URL after Back. */
      if (openRef.current === null) return;
      if (current && current.slug !== openRef.current) {
        openRef.current = current.slug;
        setOpenSlug(current.slug);
        /* replaceState, not the router. This URL is a CONSEQUENCE of where
           you have scrolled to, not a navigation, and routing it through Next
           makes it one: layouts/App/ScrollRestore scrolls to the top whenever
           asPath changes, so crossing into a book threw you straight back to
           the start of the document. The page could not be scrolled at all.

           Going direct keeps the address bar honest, adds no history entry --
           so Back still leaves the reader rather than walking book by book --
           and leaves Next's own routing to the things that really are
           navigations. */
        window.history.replaceState(
          window.history.state,
          '',
          `/bookshelf/${current.slug}/`
        );
      }
    };

    const state = readerRef.current;
    measure();
    sync();
    const onResize = () => {
      measure();
      sync();
    };
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', onResize);
      state.active = false;
    };
    /* Deliberately NOT keyed on the current book. Crossing a band changes it,
       which would tear this listener down and rebuild it -- and the teardown
       sets active=false, so the scene fell back to stack placement for a frame
       and the book snapped to the pile and back on every handover. */
  }, [isOpen, order]);

  /* The pointer tilt went with the DOM hero. It wants redoing against the
     mesh, where it can lean the real book rather than a copy of it. */


  return (
    <div
      className={styles.page}
      style={
        book
          ? {
              /* Manifest first, then whatever the scene sampled, then the
                 data file's fallback. Without the manifest at the front this
                 sits on the stale fallback whenever the sampled colour agrees
                 with the manifest, because nothing changed and so nothing was
                 reported. */
              '--accent':
                book.case?.colour || accents[book.slug] || book.cover?.accent || '#333',
              /* The manifest can name the ground outright. Mixing the case
                 colour with black is a decent guess and no more than that:
                 it turns a pale cloth into mud. */
              ...(book.case?.background && { '--pageBg': book.case.background }),
            }
          : undefined
      }
      data-open={book ? '' : undefined}
      data-dimmed={hovered != null || undefined}
    >
      {/* A book's URL is a real page and gets a real title -- the point of
          giving it one is that it can be shared. */}
      {book ? (
        <Meta
          title={book.title}
          description={book.takeaway || `${book.title} by ${book.author}`}
        />
      ) : (
        <Meta
          title="Bookshelf"
          description="What I have been reading."
        />
      )}

      {/* --- the stack ------------------------------------------------------
          Never unmounts. The focused book IS this canvas -- the scene animates
          the mesh from the pile to the hero pose and back, so tearing the
          canvas down at the end of the flight would take the book with it. */}
      <section
        className={styles.stackView}
        aria-label="Bookshelf"
        aria-hidden={phase !== 'stack' || undefined}
      >
        {/* The stack is the title. Kept for the document outline and for
              assistive tech, which still needs a heading here. */}
        <h1 className={styles.srOnly}>Bookshelf</h1>

        <ShelfSentence
          view={view}
          options={choices}
          onChange={patch => setView(v => ({ ...v, ...patch }))}
          shown={shelf.length}
          total={books.length}
          headRef={headRef}
        />

        <BookStack3D
          offsetRef={offsetRef}
          readerRef={readerRef}
          onPick={open}
          onAccent={onAccent}
          /* Released as soon as the exit STARTS, not when it finishes. Held
             until 'stack' the pile only begins coming back after the copy has
             already gone, so closing left an empty shelf for the length of a
             second flight. Open does not wait like this and closing should
             not either. */
          focusedSlug={phase === 'stack' || phase === 'exit' ? null : openSlug}
          shelf={shelfSlugs}
          headRef={headRef}
        />

        {/* A canvas cannot be tabbed through or read out. These are the real
              controls for anyone not using a mouse -- same books, same order,
              same handler -- and they give focus somewhere to return to when a
              book closes. */}
        <ul ref={pickListRef} className={styles.srOnlyList}>
          {shelf.map(b => (
            <li key={b.slug}>
              <button type="button" data-slug={b.slug} onClick={() => open(b.slug)}>
                {`${b.title} by ${b.author}`}
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* --- the reader ------------------------------------------------------
          One scrolling document holding every book, the one you opened at the
          top. Scrolling past a review carries you into the next book and the
          URL follows, so "next book" is not a control -- it is just scrolling.
          The 3D book is on the fixed canvas above; each band leaves a column
          for it. */}
      {book && (
        <div
          ref={readerElRef}
          className={styles.reader}
          data-phase={phase}
          data-dimmed={hovered != null || undefined}
          style={{
            '--accent':
              book.case?.colour || accents[book.slug] || book.cover?.accent || '#333',
            ...(book.case?.background && { '--pageBg': book.case.background }),
          }}
        >
          <Button
            secondary
            iconHoverShift
            icon="arrowLeft"
            className={styles.back}
            onClick={close}
          >
            All books
          </Button>

          {order.map(entry => {
            const stars = entry.rating != null ? (entry.rating / 2).toFixed(1) : null;
            return (
              <article
                key={entry.slug}
                data-band={entry.slug}
                className={styles.band}
                aria-label={entry.title}
                /* The colour belongs to the BAND, not the page. That is what
                   makes the change a hard horizontal edge that scrolls up
                   with the copy, rather than the whole ground changing under
                   you when the URL flips. */
                style={{
                  '--accent':
                    entry.case?.colour || accents[entry.slug] || entry.cover?.accent || '#333',
                  ...(entry.case?.background && { '--bandBg': entry.case.background }),
                }}
              >
                {/* A spacer on wide screens -- the book is drawn on the
                    canvas behind this, and this is what stops the copy running
                    over it. Narrow, there is no room for a column beside the
                    copy, so the canvas steps aside and the cover is shown
                    plainly instead. */}
                <div className={styles.hero} aria-hidden="true">
                  {entry.cover?.src && (
                    <img
                      className={styles.heroFlat}
                      src={entry.cover.src}
                      alt=""
                      loading="lazy"
                    />
                  )}
                </div>

                <div className={styles.copy}>
                  {/* Status is not shown: the values were never confirmed, and
                      this reads as fact. Rating is Tim's own. */}
                  {stars && (
                    <p className={styles.kicker}>
                      <span className={styles.rating}>{stars} / 5</span>
                    </p>
                  )}
                  <h2 className={styles.bookTitle}>{entry.title}</h2>
                  <p className={styles.author}>{entry.author}</p>

                  {entry.takeaway && (
                    <p className={styles.takeaway}>{entry.takeaway}</p>
                  )}

                  {(entry.review || []).map((para, i) => (
                    <p key={i} className={styles.para}>
                      {para}
                    </p>
                  ))}

                  <dl className={styles.meta}>
                    {fmt(entry.finishedOn) && (
                      <>
                        <dt>Finished</dt>
                        <dd>{fmt(entry.finishedOn)}</dd>
                      </>
                    )}
                    {/* When the review was written, if not when the book was
                        finished: a review can look back from today. */}
                    {fmt(entry.reviewedOn) && fmt(entry.reviewedOn) !== fmt(entry.finishedOn) && (
                      <>
                        <dt>Reviewed</dt>
                        <dd>{fmt(entry.reviewedOn)}</dd>
                      </>
                    )}
                    {fmt(entry.startedOn) && (
                      <>
                        <dt>Started</dt>
                        <dd>{fmt(entry.startedOn)}</dd>
                      </>
                    )}
                    {entry.pageCount && (
                      <>
                        <dt>Pages</dt>
                        <dd>{entry.pageCount}</dd>
                      </>
                    )}
                  </dl>

                  {entry.links?.goodreads && (
                    <Button
                      secondary
                      iconHoverShift
                      iconEnd="arrowRight"
                      href={entry.links.goodreads}
                      className={styles.goodreads}
                    >
                      On Goodreads
                    </Button>
                  )}

                  {entry.tags?.length > 0 && (
                    <ul className={styles.tags}>
                      {entry.tags.map(tag => (
                        <li key={tag}>
                          {/* The app's own tag, not a second kind invented
                              here -- it was the one thing on the page wearing
                              a border. */}
                          <Tag size="m" className={styles.tag}>
                            {tag}
                          </Tag>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            );
          })}

          {/* --- the rail, on the right ----------------------------------
              A tick per BOOK now, not per section: the sections are one
              column of prose and the rail's job is telling you where you are
              in the shelf. */}
          <nav
            className={styles.rail}
            aria-label="Books"
            /* Fits the screen however long the shelf gets. */
            style={{ '--railHeight': `${railPitch * Math.max(0, order.length - 1)}px` }}
            data-hovered={hovered != null || undefined}
            onPointerLeave={() => {
              placeTicks(null);
              setHovered(null);
            }}
          >
            <div
              className={styles.tabs}
              ref={tabsRef}
              onPointerMove={event => {
                const y = event.clientY - tabsRef.current.getBoundingClientRect().top;
                const at = nearestTick(placeTicks(y), y);
                if (at !== hovered) setHovered(at);
              }}
            >
              {order.map((entry, i) => (
                <a
                  key={entry.slug}
                  ref={el => {
                    tickRefs.current[i] = el;
                  }}
                  href={`/bookshelf/${entry.slug}/`}
                  className={styles.tick}
                  data-active={entry.slug === openSlug || undefined}
                  aria-current={entry.slug === openSlug ? 'true' : undefined}
                  /* Distance from whatever is under the cursor, so the rail
                     can swell around it rather than only marking the one tick.
                     Read by CSS as a plain number. */
                  style={{ '--near': hovered == null ? 9 : Math.abs(i - hovered) }}
                  onFocus={() => {
                    placeTicks(i * railPitch);
                    setHovered(i);
                  }}
                  onClick={event => {
                    event.preventDefault();
                    const band = readerRef.current.bands.find(b => b.slug === entry.slug);
                    if (band) window.scrollTo({ top: band.top, behavior: 'smooth' });
                  }}
                >
                  <span className={styles.tickLine} aria-hidden="true" />
                  <span className={styles.tickLabel}>{entry.title}</span>
                </a>
              ))}
            </div>

          </nav>
        </div>
      )}
    </div>
  );
}
